import React from 'react';
import { hasUnmappedUnmeContract } from '../api/unmePayout';
import { unmeContractColor } from '../../Common/utils/unmeContractColor';

// The source is a stored decimal amount; format its string without doing arithmetic.
function formatAmount(value) {
  if (value == null || value === '' || typeof value === 'boolean') return '—';
  if (typeof value === 'number' && (!Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER)) return '—';
  const raw = String(value).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return '—';
  const [integer, fraction] = raw.split('.');
  const significantFraction = fraction?.replace(/0+$/, '');
  return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${significantFraction ? `.${significantFraction}` : ''}원`;
}

function nameFor(contract) {
  const keyIsNull = contract?.contractKey === null || (contract?.contractKey === undefined && contract?.contract_key === null);
  if (keyIsNull) return '-';
  return typeof contract?.contractName === 'string' && contract.contractName.trim() ? contract.contractName.trim() : '-';
}

// A single non-zero contract can share the reconciled parent amount. A zero
// amount is eligible only when it is the sole contract; missing amounts never are.
export function getCompactPendingContract(contractSettlement, settlementId) {
  if (contractSettlement?.available !== true || contractSettlement.reconciled !== true || !Array.isArray(contractSettlement.contracts) || !contractSettlement.contracts.length) return null;
  const contracts = contractSettlement.contracts;
  const valid = contracts.every((contract) => {
    if (!contract || typeof contract !== 'object' || Array.isArray(contract)) return false;
    if (contract.settlement_id != null && settlementId != null && String(contract.settlement_id) !== String(settlementId)) return false;
    const key = contract.contractKey ?? contract.contract_key;
    if (!['PRIMARY', 'SECONDARY'].includes(key) || nameFor(contract) === '-' || hasUnmappedUnmeContract(contract)) return false;
    return formatAmount(contract.net_amount) !== '—';
  });
  if (!valid) return null;
  if (contracts.length === 1) return contracts[0];
  const nonZero = contracts.filter((contract) => !/^-?0+(?:\.0+)?$/.test(String(contract.net_amount).trim()));
  return nonZero.length === 1 ? nonZero[0] : null;
}

export function UnmePendingContractBadge({ contract }) {
  const key = contract.contractKey ?? contract.contract_key;
  return <span className={`inline-flex shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${unmeContractColor(key)}`}>{nameFor(contract)}</span>;
}

/** Stored pending breakdown. This component neither recalculates nor initiates settlement. */
export default function UnmePendingContracts({ contractSettlement, settlementId }) {
  if (contractSettlement == null) return null;
  if (contractSettlement.available !== true) {
    return <div className="mt-1 text-[11px] text-slate-500" onClick={(event) => event.stopPropagation()}>계약별 집계를 확인할 수 없습니다.</div>;
  }

  const contracts = Array.isArray(contractSettlement.contracts) ? contractSettlement.contracts : [];
  const hasMismatch = contracts.some((contract) => contract?.settlement_id != null && settlementId != null && String(contract.settlement_id) !== String(settlementId));
  if (!Array.isArray(contractSettlement.contracts) || !contracts.length || hasMismatch || contracts.some((contract) => !contract || typeof contract !== 'object' || Array.isArray(contract))) {
    return <div role="status" className="mt-1 text-[11px] font-semibold text-amber-700" onClick={(event) => event.stopPropagation()}>계약별 집계 확인 필요</div>;
  }

  const hasUnmapped = hasUnmappedUnmeContract(contractSettlement);
  const visibleContracts = contracts.filter((contract) => !hasUnmappedUnmeContract(contract));
  return <div className="mt-1 text-[11px]" onClick={(event) => event.stopPropagation()}>
    {contractSettlement.reconciled !== true && <p role="status" className="mb-1 font-semibold text-amber-700">계약별 집계 확인 필요</p>}
    {hasUnmapped && <p role="status" className="mb-1 font-semibold text-amber-700">계약 정보 확인 필요</p>}
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
    {visibleContracts.map((contract, index) => {
      const name = nameFor(contract);
      const colorKey = name === '-' ? null : (contract.contractKey ?? contract.contract_key);
      return <div key={`${contract.id ?? 'legacy'}-${index}`} className="flex min-w-0 items-center gap-1">
        <span className={`inline-flex shrink-0 rounded border px-1.5 py-0.5 font-semibold ${unmeContractColor(colorKey)}`}>{name}</span>
        <strong className="text-right tabular-nums text-slate-800">{formatAmount(contract.net_amount)}</strong>
      </div>;
    })}
    </div>
  </div>;
}
